/**
 * 聊天消息状态归并（story AC-7/AC-8，纯函数，无 React/WS 依赖，便于单测）。
 *
 * 关键不变量（复刻源 App useChatMessages，见 story Dev Notes port-risk）：
 *  - 气泡按时间升序（旧在前、新在后）；新消息 append 到末尾。
 *  - ACK 归并匹配键 = clientMsgId（服务端原样回显）。
 *  - 入站去重键 = serverMsgId 优先、其次 clientMsgId（历史 + SYNC_BATCH + 实时三路重投）。
 *  - ERROR 帧无 clientMsgId → 标记“最后一条 sending”气泡失败（粗粒度，与源一致）。
 *  - 040206（内容安全 REJECT）失败不可重试；其余失败可同 clientMsgId 重发。
 */
import type { ImFrame } from './frame';
import type { MessageItemResponse } from '@/api/im';

export type ChatBubbleStatus = 'sending' | 'sent' | 'failed';

export interface ChatBubble {
  clientMsgId: string;
  serverMsgId: string | null;
  conversationId: string;
  senderId: string;
  content: string;
  sentAtMs: number;
  status: ChatBubbleStatus;
  errorCode?: string;
  /** ACK recipientOnline === false 时为 true → 渲染“对方离线”。 */
  recipientOffline?: boolean;
  riskKeywords?: string[];
}

export interface ChatState {
  bubbles: ChatBubble[];
  /** 按 clientMsgId 关闭风险提示条（不持久；新命中再提示）。 */
  dismissedBanners: string[];
}

export const initialChatState: ChatState = { bubbles: [], dismissedBanners: [] };

export type ChatAction =
  | { type: 'LOAD_HISTORY'; items: MessageItemResponse[] }
  | { type: 'SEND'; bubble: ChatBubble }
  | { type: 'ACK'; frame: ImFrame }
  | { type: 'INBOUND'; bubble: ChatBubble }
  | { type: 'ERROR'; code: string }
  | { type: 'TIMEOUT'; clientMsgId: string }
  | { type: 'RETRY'; clientMsgId: string }
  | { type: 'DISMISS_BANNER'; clientMsgId: string };

/** 历史项（最新在前）→ 升序气泡（旧在前）。 */
export function historyToBubbles(items: MessageItemResponse[]): ChatBubble[] {
  return items
    .map(
      (m): ChatBubble => ({
        clientMsgId: `srv-${m.id ?? ''}`,
        serverMsgId: m.id ?? null,
        conversationId: m.conversationId ?? '',
        senderId: m.senderId ?? '',
        content: m.content ?? '',
        sentAtMs: m.sentAt ? Date.parse(m.sentAt) : 0,
        status: 'sent',
      }),
    )
    .slice()
    .reverse();
}

/** 构造乐观发送气泡。 */
export function createOutgoingBubble(args: {
  clientMsgId: string;
  conversationId: string;
  senderId: string;
  content: string;
  sentAtMs: number;
}): ChatBubble {
  return { ...args, serverMsgId: null, status: 'sending' };
}

/** 入站 MESSAGE / SYNC 帧 → 气泡（调用方已校验三字段齐全）。 */
export function inboundBubbleFromFrame(frame: ImFrame): ChatBubble {
  return {
    clientMsgId: `peer-${frame.serverMsgId ?? frame.clientMsgId ?? frame.sentAtMs ?? ''}`,
    serverMsgId: frame.serverMsgId ?? null,
    conversationId: frame.conversationId ?? '',
    senderId: frame.senderId ?? '',
    content: frame.content ?? '',
    sentAtMs: frame.sentAtMs ?? 0,
    status: 'sent',
    riskKeywords: frame.riskKeywords,
  };
}

/** 入站气泡是否与已有重复（serverMsgId 优先，其次 clientMsgId）。 */
function isDuplicate(bubbles: ChatBubble[], incoming: ChatBubble): boolean {
  if (incoming.serverMsgId != null && bubbles.some((b) => b.serverMsgId === incoming.serverMsgId)) {
    return true;
  }
  return bubbles.some((b) => b.clientMsgId === incoming.clientMsgId);
}

/** 040206（内容安全 REJECT）不可重试。 */
export function isRetryable(bubble: ChatBubble): boolean {
  return bubble.status === 'failed' && bubble.errorCode !== '040206';
}

export function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case 'LOAD_HISTORY':
      // 切会话/重载：整体替换气泡并清空本会话的关闭记录。
      return { bubbles: historyToBubbles(action.items), dismissedBanners: [] };

    case 'SEND':
      return { ...state, bubbles: [...state.bubbles, action.bubble] };

    case 'ACK': {
      const { clientMsgId, serverMsgId, sentAtMs, recipientOnline, riskKeywords } = action.frame;
      if (!clientMsgId) return state;
      return {
        ...state,
        bubbles: state.bubbles.map((b) =>
          b.clientMsgId === clientMsgId
            ? {
                ...b,
                serverMsgId: serverMsgId ?? b.serverMsgId,
                sentAtMs: sentAtMs ?? b.sentAtMs,
                status: 'sent',
                recipientOffline: recipientOnline === false,
                riskKeywords: riskKeywords ?? b.riskKeywords,
              }
            : b,
        ),
      };
    }

    case 'INBOUND':
      if (isDuplicate(state.bubbles, action.bubble)) return state;
      return { ...state, bubbles: [...state.bubbles, action.bubble] };

    case 'ERROR': {
      // ERROR 帧无 clientMsgId → 标记最后一条 sending。
      let idx = -1;
      for (let i = state.bubbles.length - 1; i >= 0; i--) {
        if (state.bubbles[i].status === 'sending') {
          idx = i;
          break;
        }
      }
      if (idx === -1) return state;
      const bubbles = state.bubbles.slice();
      bubbles[idx] = { ...bubbles[idx], status: 'failed', errorCode: action.code };
      return { ...state, bubbles };
    }

    case 'TIMEOUT':
      return {
        ...state,
        bubbles: state.bubbles.map((b) =>
          b.clientMsgId === action.clientMsgId && b.status === 'sending'
            ? { ...b, status: 'failed', errorCode: 'TIMEOUT' }
            : b,
        ),
      };

    case 'RETRY':
      return {
        ...state,
        bubbles: state.bubbles.map((b) =>
          b.clientMsgId === action.clientMsgId
            ? { ...b, status: 'sending', errorCode: undefined }
            : b,
        ),
      };

    case 'DISMISS_BANNER':
      if (state.dismissedBanners.includes(action.clientMsgId)) return state;
      return { ...state, dismissedBanners: [...state.dismissedBanners, action.clientMsgId] };

    default:
      return state;
  }
}
